"use client";

import { dispatchGameBalance } from "@workspace/layout-ui/src/components/Header/Balance/gameBalanceEvent";
import type { RefObject } from "react";
import { useEffect } from "react";
import toBaseCurrencyAmount from "./toBaseCurrencyAmount";

type Params = {
  containerRef: RefObject<HTMLDivElement | null>;
  walletCurrency?: string;
  attempt: number;
};

// The SDK freezes the wallet for the duration of a round and reports the balance
// on either side of it. The header lives in the layout, a sibling of this page,
// so the numbers are forwarded over a window event.
const useGameBalanceBridge = ({
  containerRef,
  walletCurrency,
  attempt,
}: Params) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const forwardBalance = (event: Event, isLocked: boolean) => {
      const { balance } = (event as GameContainerBalanceEvent).detail;
      dispatchGameBalance({
        isLocked,
        balance: toBaseCurrencyAmount(balance, walletCurrency),
      });
    };
    const handleLock = (event: Event) => forwardBalance(event, true);
    const handleUnlock = (event: Event) => forwardBalance(event, false);

    container.addEventListener("balance-lock", handleLock);
    container.addEventListener("balance-unlock", handleUnlock);
    return () => {
      container.removeEventListener("balance-lock", handleLock);
      container.removeEventListener("balance-unlock", handleUnlock);
      // Leaving mid-round takes the game away before it can unlock the wallet,
      // so release the header instead of freezing it for the rest of the session.
      dispatchGameBalance({ isLocked: false });
    };
  }, [containerRef, walletCurrency, attempt]);
};

export default useGameBalanceBridge;
